/**
 * Indexer types for on-chain data abstraction layer.
 *
 * Three distinct data sources map to three interfaces:
 * - IIndexerService: V4 subgraph data (price, volume, swaps, OHLCV, pool state, trending)
 * - IHolderIndexerService: ERC20 Transfer data (holder list, holder count)
 * - IClankerIndexerService: Clanker FeeLocker data (fee claims, accumulated fees)
 *
 * See: _bmad-output/planning-artifacts/indexer-architecture-analysis.md
 */

import { z } from "zod";

// ─── Reusable Format Validators ─────────────────────────────

/** Ethereum address: 0x + 40 hex chars (case-insensitive) */
const ethAddress = z.string().regex(/^0x[a-fA-F0-9]{40}$/, "Invalid Ethereum address");

/** Transaction/pool hash: 0x + 64 hex chars */
const bytes32Hex = z.string().regex(/^0x[a-fA-F0-9]{64}$/, "Invalid bytes32 hex");

/** Numeric string (bigint-safe): digits only */
const numericString = z.string().regex(/^\d+$/, "Must be a numeric string");

// ─── Response Wrapper ────────────────────────────────────────

export const DataSourceSchema = z.enum(["indexer", "cache", "seeded"]);
export type DataSource = z.infer<typeof DataSourceSchema>;

export const DataSourceMetaSchema = z.object({
	dataSource: DataSourceSchema,
	dataAge: z.number(), // seconds since last update
});
export type DataSourceMeta = z.infer<typeof DataSourceMetaSchema>;

export type IndexedResponse<T> = {
	data: T;
	meta: DataSourceMeta;
};

// ─── IIndexerService Types ───────────────────────────────────

export const IndexedTokenStatsSchema = z.object({
	contractAddress: ethAddress,
	price: z.number(), // USD — Token.derivedETH * Bundle.ethPriceUSD
	priceChange24h: z.number(), // % — computed from TokenDayData
	volume24h: z.number(), // USD — TokenDayData.volumeUSD
	volume7d: z.number(), // USD — sum of 7 TokenDayData entries
	marketCap: z.number(), // USD — price * totalSupply
	holderCount: z.number(), // from IHolderIndexerService (cross-service, assembled by handler)
	liquidity: z.number(), // USD — Pool.totalValueLockedUSD
	totalSupply: numericString, // bigint as string — Token.totalSupply
	feesUsd24h: z.number(), // USD — TokenDayData.feesUSD
	txCount24h: z.number(), // from TokenDayData or PoolDayData
});
export type IndexedTokenStats = z.infer<typeof IndexedTokenStatsSchema>;

export const TrendingTokenSchema = z.object({
	contractAddress: ethAddress,
	name: z.string(), // Token.name
	symbol: z.string(), // Token.symbol
	logoUrl: z.string().nullable(), // from D1 (not subgraph)
	price: z.number(), // USD
	priceChange24h: z.number(), // %
	volume24h: z.number(), // USD
	rank: z.number(), // position in trending list (1-based)
});
export type TrendingToken = z.infer<typeof TrendingTokenSchema>;

export const TradeHistoryEntrySchema = z.object({
	txHash: bytes32Hex, // Transaction.id
	timestamp: z.string(), // ISO 8601 — from Swap.timestamp
	type: z.enum(["buy", "sell"]), // derived from amount signs
	tokenAmount: z.number(), // abs(Swap.amount0) or abs(Swap.amount1)
	ethAmount: z.number(), // the paired amount (native currency side)
	priceAtTrade: z.number(), // USD — Swap.amountUSD / tokenAmount
	walletAddress: ethAddress, // Swap.origin (EOA, not router)
});
export type TradeHistoryEntry = z.infer<typeof TradeHistoryEntrySchema>;

export const TokenPriceSchema = z.object({
	contractAddress: ethAddress,
	price: z.number(), // USD
	priceChange24h: z.number(), // %
	lastUpdated: z.string(), // ISO 8601
});
export type TokenPrice = z.infer<typeof TokenPriceSchema>;

export const PriceIntervalSchema = z.enum(["1H", "4H", "1D", "1W"]);
export type PriceInterval = z.infer<typeof PriceIntervalSchema>;

export const PriceCandleSchema = z.object({
	timestamp: z.number(), // Unix seconds — period start
	open: z.number(), // USD
	high: z.number(), // USD
	low: z.number(), // USD
	close: z.number(), // USD
	volume: z.number(), // USD — period volume
});
export type PriceCandle = z.infer<typeof PriceCandleSchema>;

export const PoolStateSchema = z.object({
	poolId: bytes32Hex, // bytes32 hex — Pool.id
	contractAddress: ethAddress, // token address used as lookup key
	liquidity: numericString, // bigint as string — Pool.liquidity (uint128)
	sqrtPriceX96: numericString, // bigint as string — Pool.sqrtPrice (Q64.96)
	tick: z.number(), // Pool.tick
	feeTier: z.number(), // Pool.feeTier (basis points, dynamic for V4)
	tvlUsd: z.number(), // Pool.totalValueLockedUSD
	token0Address: ethAddress, // Pool.token0.id
	token1Address: ethAddress, // Pool.token1.id
});
export type PoolState = z.infer<typeof PoolStateSchema>;

// ─── Analytics Time-Series Types ─────────────────────────────

export const VolumeHistoryEntrySchema = z.object({
	timestamp: z.number(), // Unix seconds — period start
	volume: z.number(), // USD — period trading volume
});
export type VolumeHistoryEntry = z.infer<typeof VolumeHistoryEntrySchema>;

export const VolumeHistoryResponseSchema = z.object({
	data: z.array(VolumeHistoryEntrySchema),
	meta: DataSourceMetaSchema,
});
export type VolumeHistoryResponse = z.infer<typeof VolumeHistoryResponseSchema>;

export const HolderHistoryEntrySchema = z.object({
	timestamp: z.number(), // Unix seconds — period start
	holderCount: z.number(), // unique holder count at this point
});
export type HolderHistoryEntry = z.infer<typeof HolderHistoryEntrySchema>;

export const HolderHistoryResponseSchema = z.object({
	data: z.array(HolderHistoryEntrySchema),
	meta: DataSourceMetaSchema,
});
export type HolderHistoryResponse = z.infer<typeof HolderHistoryResponseSchema>;

// ─── Analytics History Query Params ─────────────────────────

export const AnalyticsHistoryQuerySchema = z.object({
	interval: PriceIntervalSchema,
	limit: z.coerce.number().min(1).max(200).default(30),
});
export type AnalyticsHistoryQuery = z.infer<typeof AnalyticsHistoryQuerySchema>;

// ─── IHolderIndexerService Types ─────────────────────────────

export const HolderEntrySchema = z.object({
	walletAddress: ethAddress, // ERC20 holder address (checksummed)
	balance: z.number(), // whole tokens (from bigint / 10^decimals)
	percentage: z.number(), // % of total supply
});
export type HolderEntry = z.infer<typeof HolderEntrySchema>;

// ─── IClankerIndexerService Types ────────────────────────────

export const FeeClaimEntrySchema = z.object({
	txHash: bytes32Hex,
	timestamp: z.string(), // ISO 8601
	tokenAddress: ethAddress, // token the fees were earned from
	claimAmount: numericString, // bigint as string (native currency wei)
	creatorWallet: ethAddress,
});
export type FeeClaimEntry = z.infer<typeof FeeClaimEntrySchema>;

export const AccumulatedFeesSchema = z.object({
	contractAddress: ethAddress,
	availableAmount: numericString, // bigint as string (unclaimed)
	totalClaimedAmount: numericString, // bigint as string (all-time claimed)
	lastClaimTimestamp: z.string().nullable(), // ISO 8601
});
export type AccumulatedFees = z.infer<typeof AccumulatedFeesSchema>;
